import { TypedDocumentNode, gql, useQuery } from '@apollo/client';
import DOMPurify from 'dompurify';
import { useEffect, useState } from 'react';
import { Navigate, generatePath, useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Title3 } from '@sorare/core/src/atoms/typography';
import { Gap } from '@sorare/core/src/components/marketing/Gap';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare';
import { BLOG } from '@sorare/core/src/constants/__generated__/routes';
import { ANY_SPORT_BLOG } from '@sorare/core/src/constants/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useIsVisibleInViewport } from '@sorare/core/src/hooks/ui/useIsVisibleInViewport';
import useQueryString from '@sorare/core/src/hooks/useQueryString';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import CardsOnSaleBlock from 'components/CardsOnSaleBlock';
import ClassicBlock from 'components/ClassicBlock';
import Meta from 'components/Meta';
import ScrollPopup from 'components/ScrollPopup';
import { BLOG_POST, BLOG_ROOT, BlogSport } from 'constants/routes';
import usePostImage from 'hooks/usePostImage';
import { getPostCategory } from 'lib/postCategory';

import { BackButton } from '../BackButton';
import { getPost, getPostVariables } from './__generated__/index.graphql';

const GET_POST = gql`
  query getPost($slug: ID!) {
    post(id: $slug, idType: SLUG) {
      ...getPostCategory_Post
      databaseId
      title
      date
      slug
      content
      rawExcerpt: excerpt(format: RENDERED)
      featuredImage {
        node {
          altText
          thumbnail: sourceUrl(size: MEDIUM)
          sourceUrl: sourceUrl(size: LARGE)
        }
      }
      scrollPopup {
        text
        cta
        ctaUrl
      }
      ...PostExcerpt_Meta
    }
  }
  ${Meta.frament}
  ${getPostCategory.fragments.post}
` as TypedDocumentNode<getPost, getPostVariables>;

const Content = styled.div`
  font: var(--t-20);
  line-height: 140%;

  & .has-small-font-size {
    font: var(--t-16);
  }
  & .has-medium-font-size {
    font: var(--t-20);
  }
  & .has-large-font-size {
    font: var(--t-24);
  }
  & .has-x-large-font-size {
    font: var(--t-32);
  }
  & .has-xx-large-font-size {
    font: var(--t-48);
  }

  & a {
    text-decoration: underline;
  }

  & figure {
    margin: 1em 0 1em 0;
    img,
    div,
    iframe {
      width: 100%;
    }
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    margin-top: revert;
    line-height: 1.2em;
  }

  & ul {
    padding-left: 0;

    li {
      list-style-type: disc;
      margin-left: 30px;
    }
  }
`;

const Title = styled(Title3)`
  display: inline-flex;
  min-width: 70%;
  flex-direction: column;
  background: var(--c-black);
  margin-top: -2em;
  padding: var(--double-unit) var(--triple-unit);

  @media (${tabletAndAbove}) {
    width: min-content;
  }
`;

const MetaAndSharing = styled(SBHorizontal)`
  padding-left: var(--triple-unit);
  margin-bottom: var(--quadruple-unit);
`;

const Share = styled(Horizontal)`
  justify-content: center;
`;

interface Props {
  preview?: boolean;
  sportCategory?: BlogSport;
}

declare global {
  interface Window {
    twttr: any;
  }
}

const loadScript = async (src: string) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve(undefined);
      return;
    }

    const checkoutScript = document.createElement('script');
    checkoutScript.src = src;
    checkoutScript.onload = resolve;
    checkoutScript.onerror = reject;
    document.head.appendChild(checkoutScript);
  });
};

export const Post = ({ preview, sportCategory }: Props) => {
  const { slug } = useParams();

  const wp_token = useQueryString('wp_token');
  const { loading, data, error } = useQuery(GET_POST, {
    variables: { slug: slug! },
    context: preview
      ? {
          headers: {
            'X-WP-Nonce': wp_token,
          },
          credentials: 'include',
        }
      : undefined,
    skip: !slug,
  });
  const track = useEvents();
  const { setPageMetadata } = useSeoContext();
  const { showNotification } = useSnackNotificationContext();
  const { handleRef: bottomRef, isVisible } =
    useIsVisibleInViewport<HTMLDivElement>();
  const [readTracked, setReadTracked] = useState<boolean>(false);

  useEffect(() => {
    loadScript('https://platform.twitter.com/widgets.js').then(() => {
      setTimeout(() => window.twttr?.widgets.load(), 1000);
    });
  }, []);

  useEffect(() => {
    if (data?.post?.slug) {
      track('View Blog Post', { slug: data.post.slug });
    }
  }, [data?.post?.slug, track]);

  useEffect(() => {
    if (isVisible && !readTracked && data?.post?.slug) {
      track('Read Blog Post', { slug: data.post.slug });
      setReadTracked(true);
    }
  }, [data?.post?.slug, isVisible, readTracked, setReadTracked, track]);

  const postImage = usePostImage(data?.post, true);
  const description = DOMPurify.sanitize(data?.post?.rawExcerpt || '', {
    ALLOWED_TAGS: [],
  });

  useEffect(() => {
    if (data?.post?.title) {
      setPageMetadata(`${data.post.title} - Sorare Blog`, {
        description,
        img: postImage?.url,
      });
    }
  }, [data?.post, description, postImage?.url, setPageMetadata]);

  useEffect(() => {
    if (error) {
      showNotification('errors', { errors: [error.message] });
    }
  }, [error, showNotification]);

  useEffect(() => {
    const content = data?.post?.content;
    if (!content) return;

    for (const match of content.matchAll(/<a id="pd_a_([0-9]+)"><\/a>/g)) {
      const [, id] = match;
      loadScript(`https://secure.polldaddy.com/p/${id}.js`).then(() => {
        const poll = document.getElementById(`PDI_container${id}`);
        if (poll) {
          // cleanup the poll container to keep only the actual poll
          poll.parentElement!.replaceChildren(poll);

          // if the user has already voted, go to the results view
          if (document.cookie.includes(`PD_poll_${id}`)) {
            (window as any)[`PD_vote${id}`]?.(1);
          }
        }
      });
    }
  }, [data]);

  const errorElement = sportCategory ? (
    <Navigate to={generatePath(BLOG_ROOT, { sport: sportCategory })} />
  ) : (
    <Navigate to={BLOG} />
  );

  if (error) {
    return errorElement;
  }

  if (loading || !data) return <LoadingIndicator fullHeight />;

  const { post } = data;

  if (!post) {
    return errorElement;
  }

  // Redirect to sport specific blog post if possible
  const sport = getPostCategory(post);
  if (!preview && sport && sportCategory !== sport) {
    return (
      <Navigate
        to={generatePath(BLOG_POST, {
          slug: slug!,
          sport,
        })}
        replace
      />
    );
  }

  const { title, content, scrollPopup } = post;

  const sharingProps = {
    title: title!,
    description,
    message: title || undefined,
    // eslint-disable-next-line no-restricted-properties
    url: window.location.href,
    image: {
      post: postImage?.url || null,
      story: postImage?.url || null,
      square: postImage?.url || null,
    },
    trackingEventName: socialShareEventName.SHARE_BLOG_POST,
    trackingEventContext: socialShareEventContext.BLOG,
  };

  return (
    <>
      <BackButton
        safePath={generatePath(ANY_SPORT_BLOG, {
          sport: sportCategory ?? 'football',
        })}
      />
      <Vertical>
        {postImage?.url && <img src={postImage.url} alt={title || ''} />}
        <Title>{title}</Title>
        <MetaAndSharing>
          <Meta excerpt={post} />
          <SocialShare
            {...sharingProps}
            renderButton={({ ShareButton, label, Icon }) => (
              <ShareButton size="small">
                {Icon}
                <span>{label}</span>
              </ShareButton>
            )}
          />
        </MetaAndSharing>

        <Content>
          {content!
            .split(/(<data-sorare.+?>.+?<\/data-sorare.+?>)/)
            .map((part, index) => {
              const key = `${slug}-${index}`;
              if (part.startsWith('<data-sorare-cards-on-sale')) {
                return <CardsOnSaleBlock key={key} data={part} />;
              }
              return (
                <ClassicBlock
                  key={key}
                  data={part}
                  postImageUrl={postImage?.url}
                />
              );
            })}
          {scrollPopup && <ScrollPopup {...scrollPopup} />}
          <div ref={bottomRef} />
        </Content>

        <Gap size="md" />

        <Share>
          <SocialShare
            {...sharingProps}
            renderButton={({ ShareButton, label, Icon }) => (
              <ShareButton size="large" color="tertiary">
                {Icon}
                <span>{label}</span>
              </ShareButton>
            )}
          />
        </Share>
      </Vertical>
    </>
  );
};
